/**
 * A dataset's name and description in a dialog, for creating one and for editing it later.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldInput } from '@/modules/simulator/ui/field';
import { PillButton } from '@/modules/simulator/ui/pill-button';

const MAX_NAME_CHARS = 120;
const MAX_DESCRIPTION_CHARS = 2000;

/** The name cut to the limit at a word, so a long suggestion never ends mid-word. */
const fitName = (text: string): string => {
	const trimmed = text.trim();
	if (trimmed.length <= MAX_NAME_CHARS) return trimmed;
	const cut = trimmed.slice(0, MAX_NAME_CHARS + 1);
	const lastSpace = cut.lastIndexOf(' ');
	return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut.slice(0, MAX_NAME_CHARS)).replace(/[\s,;:.-]+$/, '');
};

interface DatasetDetailsDialogProps {
	open: boolean;
	title: string;
	description: ReactNode;
	submitLabel: string;
	initialName: string;
	initialDescription: string | null;
	busy: boolean;
	onOpenChange: (open: boolean) => void;
	/** The trimmed name, and the description or null when none was written. */
	onSubmit: (name: string, description: string | null) => void;
}

export const DatasetDetailsDialog = ({
	open,
	title,
	description,
	submitLabel,
	initialName,
	initialDescription,
	busy,
	onOpenChange,
	onSubmit
}: DatasetDetailsDialogProps) => {
	const [name, setName] = useState('');
	const [text, setText] = useState('');

	useEffect(() => {
		if (!open) return;
		setName(fitName(initialName));
		setText(initialDescription ?? '');
	}, [open, initialName, initialDescription]);

	const trimmedName = name.trim();
	const submit = (event: FormEvent) => {
		event.preventDefault();
		if (!trimmedName || busy) return;
		onSubmit(trimmedName, text.trim() || null);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-md'>
				<form onSubmit={submit} className='flex flex-col gap-4'>
					<DialogHeader>
						<DialogTitle className='text-[15px]'>{title}</DialogTitle>
						<DialogDescription className='text-xs leading-4 text-gray-800'>{description}</DialogDescription>
					</DialogHeader>
					<Field label='Name'>
						<FieldInput autoFocus value={name} maxLength={MAX_NAME_CHARS} onChange={(event) => setName(event.target.value)} />
					</Field>
					<Field label='Description (optional)'>
						<Textarea
							value={text}
							maxLength={MAX_DESCRIPTION_CHARS}
							onChange={(event) => setText(event.target.value)}
							className='min-h-20 rounded-lg border-gray-500 text-[13px] leading-5'
						/>
					</Field>
					<DialogFooter>
						<PillButton type='button' variant='ghost' disabled={busy} onClick={() => onOpenChange(false)}>
							Cancel
						</PillButton>
						<PillButton type='submit' variant='primary' disabled={!trimmedName || busy}>
							{busy && <Loader2 className='size-3 animate-spin' />}
							{submitLabel}
						</PillButton>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
